import {Box, Button, Stack, Typography, useTheme} from '@mui/material';
import {BankIDLogo, ResursLoading} from '@resurs-internal/mui';
import {BankIDQRCode} from './BankIDQRCode';
import React, {useEffect, useMemo, useState} from 'react';
import {useTranslate} from '../../hooks';
import {BankIDCodeRequest, useBankIDCode} from '../../hooks/useBankIDCode';
import {
  bankidIntentSuffix,
  chromeIOSURL,
  chromeURL,
  edgeURL,
  firefoxURL,
  isAndroid,
  isChrome,
  isChromeWebView,
  isEdge,
  isFirefox,
  isIOS,
  isLinux,
  isSafari,
  isSamsung,
  samsungURL,
} from '../../services/browser';

export type BankIDViewCreatedProps = BankIDCodeRequest & {
  onCancel: () => void;
  autoStartToken?: string;
  expiresAt?: string;
  // QR code from the status poll (merged mode). When provided, the legacy /qr
  // poll below is disabled - callers that don't pass this (still on the old
  // backend mode) keep polling it exactly as before.
  qrCode?: string;
  // When true (new backend mode), the BankID app uses the server-side returnUrl
  // configured by the backend on the /auth request, so the autostart URL must
  // not carry its own `redirect=` query param.
  useReturnUrl?: boolean;
};

function getSecondsRemaining(expiresAt?: string): number {
  if (!expiresAt) return Number.NaN;
  const diffMs = new Date(expiresAt).getTime() - Date.now();
  return Math.max(0, Math.floor(diffMs / 1000));
}

function formatTimeLeft(
  totalSeconds: number,
  t: ReturnType<typeof useTranslate>,
): string {
  if (Number.isNaN(totalSeconds)) return '';

  if (totalSeconds < 0) totalSeconds = 0;

  const minutes = Math.ceil(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (totalSeconds < 60) {
    return `${seconds} ${t('signature.countdown.seconds.left')}`;
  }

  // Special case: keep displaying '2 minutes left', until seconds only display kick in
  // otherwise there is a brief second or so of displaying '1 minutes left'
  return `${minutes == 1 ? 2 : minutes} ${t('signature.countdown.minutes.left')}`;
}

export const BankIDViewCreated: React.FC<BankIDViewCreatedProps> = ({
  type,
  token,
  onCancel,
  autoStartToken,
  expiresAt,
  qrCode,
  authToken,
  bft,
  host,
  useReturnUrl,
  refreshAuthToken,
}) => {
  const theme = useTheme();
  const translate = useTranslate();
  const [openingBankId, setOpeningBankId] = useState(false);
  const useLegacyQrPoll = qrCode === undefined;
  const bankIDCode = useBankIDCode({
    type,
    token,
    authToken,
    bft,
    host,
    enabled: useLegacyQrPoll && !openingBankId,
    refreshAuthToken,
  });

  const [secondsLeft, setSecondsLeft] = useState(() =>
    getSecondsRemaining(expiresAt),
  );
  const [hideCountdown, setHideCountdown] = useState(false);

  useEffect(() => {
    let hideCountdownTimeoutId: ReturnType<typeof setTimeout> | undefined;
    const interval = setInterval(() => {
      const remaining = getSecondsRemaining(expiresAt);
      setSecondsLeft(remaining);
      if (remaining <= 0) {
        clearInterval(interval);
        hideCountdownTimeoutId = setTimeout(() => setHideCountdown(true), 5000);
      }
    }, 1000);
    return () => {
      clearInterval(interval);
      clearTimeout(hideCountdownTimeoutId);
    };
  }, [expiresAt]);

  const timeLeftLabel = expiresAt ? formatTimeLeft(secondsLeft, translate) : '';

  const bankIDURL = useMemo(() => {
    if (useReturnUrl) {
      // Backend is in returnUrl mode — BankID resolves the return target from
      // the server-side returnUrl, so omit the autostart redirect param.
      // iOS Chrome/Safari: use bankid:/// so the browser does not navigate away
      // from the auth page; the polling tab stays at its current URL. For Chrome
      // the server returnUrl (googlechromes://) focuses the existing tab. For
      // Safari no returnUrl is set, so BankID shows its done screen and the user
      // returns to Safari where the polling tab is still active.
      if (isIOS && (isChrome || isSafari)) {
        return `bankid:///?autostarttoken=${autoStartToken}`;
      }
      // Samsung Internet does not resolve Android App Links (it renders
      // app.bankid.com as a page → "kunde inte startas"); it only opens apps
      // via the bankid:// scheme. No redirect param — the server-side returnUrl
      // governs the return in this mode.
      if (isSamsung) {
        return `bankid:///?autostarttoken=${autoStartToken}`;
      }
      // Chrome WebView also cannot resolve Android App Links — the WebView
      // renders app.bankid.com in-page. Use bankid:// to let the OS launch
      // the BankID app directly. No redirect param — returnUrl governs return.
      if (isChromeWebView) {
        return `bankid:///?autostarttoken=${autoStartToken}`;
      }
      // Edge on Android does not resolve App Links and blocks bankid:// — use
      // the Android Intent URL. No redirect param — returnUrl governs return.
      if (isAndroid && isEdge) {
        return `intent:///?autostarttoken=${autoStartToken}${bankidIntentSuffix}`;
      }
      // Chrome-for-Android / iOS resolve the universal/app link.
      const base =
        isIOS || isAndroid ? 'https://app.bankid.com/' : 'bankid:///';
      return `${base}?autostarttoken=${autoStartToken}`;
    }
    // https://developers.bankid.com/getting-started/frontend/autostart#mobile-device
    // iOS: redirect must have a value, using browser-specific schemes
    if (isIOS && isSafari) {
      // Use bankid:/// with no redirect so Safari stays at the auth page. After
      // BankID auth its done screen is shown; the user returns to Safari where the
      // polling tab is still active and completes auth. A redirect URL would open
      // a new Safari tab (losing the original session's state) when Chrome is set
      // as the default browser.
      return `bankid:///?autostarttoken=${autoStartToken}`;
    }
    if (isIOS && isChrome) {
      // Use bankid:/// (not https://app.bankid.com/) so Chrome does not navigate
      // away from the auth page — custom schemes are not navigable by Chrome, so
      // the tab stays at its current URL. redirect=googlechromes:// (bare, no URL)
      // simply brings Chrome to the foreground without opening a new tab or
      // navigating anywhere, so the original polling tab resumes and completes auth.
      return `bankid:///?autostarttoken=${autoStartToken}&redirect=${chromeIOSURL}`;
    }
    if (isIOS && isFirefox) {
      return `https://app.bankid.com/?autostarttoken=${autoStartToken}&redirect=${encodeURIComponent(`firefox://open-url?url=${encodeURIComponent(window.location.href)}`)}`;
    }
    if (isIOS) {
      return `https://app.bankid.com/?autostarttoken=${autoStartToken}&redirect=null`;
    }
    // Chrome WebView cannot resolve Android App Links — use bankid:// scheme.
    if (isChromeWebView) {
      return `bankid:///?autostarttoken=${autoStartToken}&redirect=null`;
    }
    // Edge on Android does not resolve App Links and blocks bankid:// — use
    // the Android Intent URL.
    if (isAndroid && isEdge) {
      return `intent:///?autostarttoken=${autoStartToken}&redirect=null${bankidIntentSuffix}`;
    }
    // Android: redirect=null lets BankID return to the calling app via task stack
    if (isAndroid) {
      return `https://app.bankid.com/?autostarttoken=${autoStartToken}&redirect=null`;
    }
    // Desktop browsers
    if (isChrome) {
      return `bankid:///?autostarttoken=${autoStartToken}&redirect=${chromeURL}`;
    }
    if (isEdge) {
      return `bankid:///?autostarttoken=${autoStartToken}&redirect=${edgeURL}`;
    }
    if (isFirefox) {
      return `bankid:///?autostarttoken=${autoStartToken}&redirect=${firefoxURL}`;
    }
    if (isSamsung) {
      return `bankid:///?autostarttoken=${autoStartToken}&redirect=${samsungURL}`;
    }
    return `bankid:///?autostarttoken=${autoStartToken}&redirect=null`;
  }, [autoStartToken, useReturnUrl]);

  const titleKey =
    type === 'auth' ? 'auth.bankid.title' : 'signature.bankid.title';

  if (openingBankId) {
    // Once the user has launched BankID on this device, stop showing the QR
    // code — switching back to the browser (either automatically or
    // manually) would otherwise briefly re-show the QR screen before the
    // next status poll lands. Cancel stays available. A
    // Cancel click that races an authentication which already completed
    // server-side is handled by LoginMBIDPage (a 409 there is treated as a
    // no-op — the status poll redirects normally).
    return (
      <Stack spacing={{xs: 0, sm: 2}} alignItems={'center'}>
        <Stack
          direction={'column'}
          spacing={{xs: 0.5, sm: 1}}
          alignItems={'center'}>
          <Box style={{color: theme.palette.text.primary}}>
            <BankIDLogo
              size={'xxl'}
              color={
                theme.palette.mode === 'dark'
                  ? theme.palette.text.primary
                  : undefined
              }
            />
          </Box>
          <Typography variant={'h3'} textAlign={'center'} component={'h1'}>
            {translate(titleKey)}
          </Typography>
        </Stack>
        <Stack spacing={2} alignItems={'center'} py={4}>
          <ResursLoading size={'xl'} />
          <Typography variant={'body1'} textAlign={'center'}>
            {type === 'auth'
              ? translate('auth.bankid.openingBankId')
              : translate('signature.bankid.openingBankId')}
          </Typography>
        </Stack>
        <Button variant={'text'} onClick={onCancel}>
          {translate('common.cancel')}
        </Button>
      </Stack>
    );
  }

  return (
    <Stack spacing={{xs: 0, sm: 2}} alignItems={'center'}>
      <Stack
        direction={'column'}
        spacing={{xs: 0.5, sm: 1}}
        alignItems={'center'}>
        <Box style={{color: theme.palette.text.primary}}>
          <BankIDLogo
            size={'xxl'}
            color={
              theme.palette.mode === 'dark'
                ? theme.palette.text.primary
                : undefined
            }
          />
        </Box>
        <Typography variant={'h3'} textAlign={'center'} component={'h1'}>
          {translate(titleKey)}
        </Typography>
        <Typography variant={'body1'} textAlign={'center'}>
          {type === 'auth'
            ? translate('auth.bankid.subtitle')
            : translate('signature.bankid.subtitle')}
        </Typography>
      </Stack>
      <BankIDQRCode
        isLoading={useLegacyQrPoll && bankIDCode.query.isLoading}
        code={(useLegacyQrPoll ? bankIDCode.query.data?.qrCode : qrCode) ?? ''}
      />
      {!hideCountdown && expiresAt && (
        <Typography
          variant={'body2'}
          textAlign={'center'}
          color={'text.secondary'}>
          {timeLeftLabel || ' '}
        </Typography>
      )}
      <Stack direction={'column'} spacing={{xs: 1, sm: 2}}>
        <Button
          variant={'outlined'}
          disabled={!autoStartToken || isLinux}
          href={isLinux ? undefined : bankIDURL}
          onClick={() => setOpeningBankId(true)}
          sx={{textAlign: 'center'}}>
          <Typography variant={'button'}>
            {type === 'auth'
              ? translate('auth.bankid.button')
              : translate('signature.bankid.button')}
          </Typography>
        </Button>
        {isLinux && (
          <Typography
            variant={'body2'}
            textAlign={'center'}
            color={'text.secondary'}>
            {translate('common.bankid.linux-hint')}
          </Typography>
        )}
        <Button variant={'text'} onClick={onCancel}>
          {translate('common.cancel')}
        </Button>
      </Stack>
    </Stack>
  );
};
